import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { useState } from 'react'

import {
  BacFalsePositiveReason,
  BACObj,
} from '../../../providers/PatientChartPage/BACProvider'
import { themeColors } from '../../../theme'

const REASON_OPTIONS: { value: BacFalsePositiveReason; label: string }[] = [
  { value: BacFalsePositiveReason.FalsePositive, label: 'False Positive' },
  {
    value: BacFalsePositiveReason.NotByPatient,
    label: 'BAC test not done by patient',
  },
]

interface BacFalsePositiveButtonProps {
  bacRecordId: number
  routedPatientId: string | undefined
  reviewBacRecord: (
    bacRecordId: number,
    falsePositiveReason: BacFalsePositiveReason
  ) => Promise<void>
  /** Fired after a successful submit — e.g. to refresh the record list. */
  getBacRecords: (patientId: number) => Promise<BACObj[]>
  disabled?: boolean
  setFilteredBacWithVideos?: React.Dispatch<React.SetStateAction<BACObj[]>>
}

export default function BacFalsePositiveButton({
  bacRecordId,
  routedPatientId,
  reviewBacRecord,
  getBacRecords,
  setFilteredBacWithVideos,
  disabled = false,
}: BacFalsePositiveButtonProps) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<BacFalsePositiveReason | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = reason !== null && !submitting

  const resetState = () => {
    setReason(null)

    setError(null)
    setSubmitting(false)
  }

  const handleOpen = () => {
    resetState()
    setOpen(true)
  }

  const handleClose = () => {
    if (submitting) return // don't let it close mid-request
    setOpen(false)
  }

  const handleSubmit = async () => {
    if (!canSubmit || reason === null) return

    setSubmitting(true)
    setError(null)

    try {
      await reviewBacRecord(bacRecordId, reason)
      setOpen(false)
      if (routedPatientId && setFilteredBacWithVideos) {
        const updatedBacRecords = await getBacRecords(parseInt(routedPatientId))
        setFilteredBacWithVideos(updatedBacRecords)
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Something went wrong. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Button
        variant='contained'
        style={{
          fontSize: '16px',
          marginTop: '10px',
          width: '180px',
          backgroundColor: themeColors.red,
          borderColor: 'transparent',
        }}
        onClick={handleOpen}
        disabled={disabled}
      >
        Mark False Positive
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xs'>
        <DialogTitle>Mark as false positive</DialogTitle>

        <DialogContent>
          <FormControl component='fieldset' fullWidth disabled={submitting}>
            <FormLabel component='legend' sx={{ mb: 1 }}>
              Select a reason
            </FormLabel>
            <RadioGroup
              value={reason === null ? '' : String(reason)}
              onChange={(e) =>
                setReason(Number(e.target.value) as BacFalsePositiveReason)
              }
            >
              {REASON_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={String(opt.value)}
                  control={<Radio />}
                  label={
                    opt.label === 'Other'
                      ? 'Other (Description is Required)'
                      : opt.label
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {error && (
            <Alert severity='error' sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color='inherit' />
              ) : undefined
            }
          >
            {submitting ? 'Submitting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
